import { useCallback, useEffect, useRef } from 'react';
import { Virtualizer } from '@tanstack/react-virtual';
import { useAdditionalInfos } from '../hooks/use-additional-infos/use-additional-infos';
import { AppName, useAppName } from '@m43/core';

export const useUpdateVisibleAsRead = (
    enabled: boolean,
    virtualizer: Virtualizer<HTMLDivElement, Element>
): void => {
    const { list: data, hasUnread, markAsRead } = useAdditionalInfos();

    const running = useRef(false);
    const virtualItems = virtualizer.getVirtualItems();
    const isCadNext = useAppName() === AppName.CAD_NEXT;
    const readInfos = useCallback(() => {
        // Don't run if disabled or currently running.
        if (!enabled || running.current) return;
        // Don't run without both unreadInfos and virtualItems
        if (!hasUnread) return;
        if (!Array.isArray(virtualItems) || virtualItems.length === 0) return;
        // Start running, get the visible infos.
        running.current = true;
        const visibleInfos = data.slice(
            virtualItems[0].index,
            virtualItems[virtualItems.length - 1].index + 1
        );
        // Mark them as read, don't block the next run.
        markAsRead(visibleInfos).finally(() => {
            running.current = false;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [virtualItems, data, enabled, running, hasUnread]);

    useEffect(() => {
        readInfos();
    }, [readInfos]);

    if (!isCadNext) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
            // The effect above won't trigger initially when the component
            // is created with unread infos. We need to repeatedly check
            // if any infos are unread. This is _mostly_ safe because
            // `readInfos` does multiple checks before taking any action, so
            // will only run when it actually needs to.
            const interval = setInterval(() => {
                readInfos();
            }, 500);
            return () => clearInterval(interval);
        }, [readInfos]);
    }
};
